import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  FormHelperText
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Group as GroupIcon,
  Public as PublicIcon,
  Preview as PreviewIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import AdminLayout from './components/AdminLayout';
import FormRichTextField from '../../components/forms/FormRichTextField';
import RichTextDisplay from '../../components/common/RichTextDisplay';
import { FormProvider, useForm } from 'react-hook-form';
import * as messageService from '../../services/messageService';
import * as sessionService from '../../services/sessionService';

type Message = messageService.Message;

interface Session {
  id: number;
  title: string;
  startDate: string;
  endDate: string;
}

const MessageManagementPage: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMessage, setEditingMessage] = useState<Message | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState<Message | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewMessage, setPreviewMessage] = useState<Message | null>(null);


  // Form methods for rich text editor
  const formMethods = useForm({
    defaultValues: {
      title: '',
      content: '',
      messageType: 'global',
      targetSessionId: '',
      isActive: true
    }
  });

  useEffect(() => {
    fetchMessages();
    fetchSessions();
  }, []);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await messageService.getAllMessages();
      setMessages(response.data);
      setError(null);
    } catch (err: unknown) {
      console.error('Error fetching messages:', err);
      setError((err as Error)?.message || 'Failed to fetch messages');
    } finally {
      setLoading(false);
    }
  };

  const fetchSessions = async () => {
    try {
      const sessions = await sessionService.getAllSessions();
      setSessions(sessions);
    } catch (err: unknown) {
      console.error('Error fetching sessions:', err);
    }
  };

  const handleCreateMessage = () => {
    setEditingMessage(null);
    const defaultValues = {
      title: '',
      content: '',
      messageType: 'global' as 'global' | 'session_specific',
      targetSessionId: '',
      isActive: true
    };
    formMethods.reset(defaultValues);
    setDialogOpen(true);
  };

  const handleEditMessage = (message: Message) => {
    setEditingMessage(message);
    const editValues = {
      title: message.title,
      content: message.content,
      messageType: message.messageType,
      targetSessionId: message.targetSessionId?.toString() || '',
      isActive: message.isActive
    };
    formMethods.reset(editValues);
    setDialogOpen(true);
  };

  const handleDeleteMessage = (message: Message) => {
    setMessageToDelete(message);
    setDeleteConfirmOpen(true);
  };

  const handlePreviewMessage = (message: Message) => {
    setPreviewMessage(message);
    setPreviewOpen(true);
  };

  const handleToggleStatus = async (message: Message) => {
    try {
      await messageService.toggleMessageStatus(message.id);
      fetchMessages();
    } catch (err: unknown) {
      console.error('Error toggling message status:', err);
      setError((err as Error)?.message || 'Failed to toggle message status');
    }
  };

  const handleSaveMessage = async () => {
    try {
      const formValues = formMethods.getValues();
      const messageData = {
        title: formValues.title,
        content: formValues.content,
        messageType: formValues.messageType as 'global' | 'session_specific',
        targetSessionId: formValues.messageType === 'session_specific' ? parseInt(formValues.targetSessionId) : undefined,
        isActive: formValues.isActive
      };

      if (editingMessage) {
        await messageService.updateMessage(editingMessage.id, messageData);
      } else {
        await messageService.createMessage(messageData);
      }

      setDialogOpen(false);
      fetchMessages();
      setError(null);
    } catch (err: unknown) {
      console.error('Error saving message:', err);
      setError((err as Error)?.message || 'Failed to save message');
    }
  };

  const confirmDelete = async () => {
    if (!messageToDelete) return;

    try {
      await messageService.deleteMessage(messageToDelete.id);
      setDeleteConfirmOpen(false);
      setMessageToDelete(null);
      fetchMessages();
      setError(null);
    } catch (err: unknown) {
      console.error('Error deleting message:', err);
      setError((err as Error)?.message || 'Failed to delete message');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <AdminLayout>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" component="h1">
            Message Management
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateMessage}
          >
            Create Message
          </Button>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Manage announcements and messages for your students
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Target</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created By</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {messages.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography variant="body1" color="text.secondary">
                          No messages found. Create your first message to get started.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    messages.map((message) => (
                      <TableRow key={message.id}>
                        <TableCell>
                          <Box>
                            <Typography variant="body1" fontWeight="medium">
                              {message.title}
                            </Typography>
                            <Box sx={{ mt: 0.5 }}>
                              <RichTextDisplay 
                                content={message.content}
                                preview={true}
                                maxLength={100}
                                sx={{ 
                                  fontSize: '0.875rem',
                                  color: 'text.secondary',
                                  '& *': { fontSize: 'inherit !important' }
                                }}
                              />
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={message.messageType === 'global' ? <PublicIcon /> : <GroupIcon />}
                            label={message.messageType === 'global' ? 'Global' : 'Session'}
                            color={message.messageType === 'global' ? 'primary' : 'secondary'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {message.messageType === 'session_specific' ? (
                            <Typography variant="body2">
                              {message.sessionTitle || `Session ${message.targetSessionId}`}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              All users
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={message.isActive ? 'Active' : 'Inactive'}
                            color={message.isActive ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {message.createdByName || 'Unknown'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(message.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Preview">
                              <IconButton
                                size="small"
                                onClick={() => handlePreviewMessage(message)}
                                color="info"
                              >
                                <PreviewIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={message.isActive ? 'Deactivate' : 'Activate'}>
                              <IconButton
                                size="small"
                                onClick={() => handleToggleStatus(message)}
                                color={message.isActive ? 'warning' : 'success'}
                              >
                                {message.isActive ? <VisibilityOffIcon /> : <VisibilityIcon />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleEditMessage(message)}
                                color="primary"
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteMessage(message)}
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Message Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingMessage ? 'Edit Message' : 'Create New Message'}
        </DialogTitle>
        <DialogContent>
          <FormProvider {...formMethods}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
              <TextField
                label="Title"
                fullWidth
                {...formMethods.register('title', { required: 'Title is required' })}
                error={!!formMethods.formState.errors.title}
                helperText={formMethods.formState.errors.title?.message}
              />

              <FormRichTextField
                name="content"
                label="Content"
                required
                helperText="Use the toolbar to format text and add YouTube videos"
                rows={6}
              />

              <FormControl fullWidth>
                <InputLabel>Message Type</InputLabel>
                <Select
                  {...formMethods.register('messageType')}
                  value={formMethods.watch('messageType')}
                  label="Message Type"
                  onChange={(e) => {
                    formMethods.setValue('messageType', e.target.value);
                    if (e.target.value === 'global') {
                      formMethods.setValue('targetSessionId', '');
                    }
                  }}
                >
                  <MenuItem value="global">Global (All Users)</MenuItem>
                  <MenuItem value="session_specific">Session Specific</MenuItem>
                </Select>
              </FormControl>

              {formMethods.watch('messageType') === 'session_specific' && (
                <FormControl fullWidth>
                  <InputLabel>Target Session</InputLabel>
                  <Select
                    {...formMethods.register('targetSessionId', { 
                      required: formMethods.watch('messageType') === 'session_specific' ? 'Target session is required' : false 
                    })}
                    value={formMethods.watch('targetSessionId')}
                    label="Target Session"
                    error={!!formMethods.formState.errors.targetSessionId}
                  >
                    {sessions.map((session) => (
                      <MenuItem key={session.id} value={session.id.toString()}>
                        {session.title}
                      </MenuItem>
                    ))}
                  </Select>
                  {formMethods.formState.errors.targetSessionId && (
                    <FormHelperText error>
                      {formMethods.formState.errors.targetSessionId.message}
                    </FormHelperText>
                  )}
                </FormControl>
              )}

              <FormControlLabel
                control={
                  <Switch
                    {...formMethods.register('isActive')}
                    checked={formMethods.watch('isActive')}
                    onChange={(e) => formMethods.setValue('isActive', e.target.checked)}
                  />
                }
                label="Active"
              />
            </Box>
          </FormProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveMessage} variant="contained">
            {editingMessage ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the message "{messageToDelete?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Message Preview Dialog */}
      <Dialog 
        open={previewOpen} 
        onClose={() => setPreviewOpen(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { maxHeight: '90vh' }
        }}
      >
        {previewMessage && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1, mr: 2 }}>
                  <Typography variant="h5" component="div" gutterBottom>
                    {previewMessage.title}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      icon={previewMessage.messageType === 'global' ? <PublicIcon /> : <GroupIcon />}
                      label={previewMessage.messageType === 'global' ? 'Global Message' : 'Session Specific'}
                      size="small"
                      color={previewMessage.messageType === 'global' ? 'primary' : 'secondary'}
                    />
                    {previewMessage.messageType === 'session_specific' && previewMessage.sessionTitle && (
                      <Chip
                        label={previewMessage.sessionTitle}
                        size="small"
                        variant="outlined"
                      />
                    )}
                    <Chip
                      label={previewMessage.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={previewMessage.isActive ? 'success' : 'default'}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Created by {previewMessage.createdByName || 'Unknown'} on {formatDate(previewMessage.createdAt)}
                  </Typography>
                </Box>
                <IconButton onClick={() => setPreviewOpen(false)}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <RichTextDisplay content={previewMessage.content} />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPreviewOpen(false)}>
                Close
              </Button>
              <Button 
                onClick={() => {
                  setPreviewOpen(false);
                  handleEditMessage(previewMessage);
                }}
                variant="contained"
                startIcon={<EditIcon />}
              >
                Edit Message
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </AdminLayout>
  );
};

export default MessageManagementPage;